import { z } from 'zod'
import type { ApiClient } from './client'

/**
 * Workbook folders — POST /{clusterId}/api/directory/list {scope_id}
 *
 * The legacy workbook list's folder tree (its FolderStructure sider). Captured
 * live 2026-09-24 (docs/inventory/detections-capture.json, directory_list):
 * `data` is the root folders; children nest under `nestedFoldArr`, as deep
 * as the tenant made them (three levels in the capture). No per-folder
 * workbook counts come back.
 *
 * A folder's `id` is what workbook/list filters on — addfilters
 * `{filter_key:'path', filter_operand:'=', filter_value:<folder id>}` —
 * and that filter matches the folder's OWN workbooks, not its subtree.
 */

interface FolderWire {
  id?: string | number
  name?: string
  folderName?: string
  path?: string
  nestedFoldArr?: FolderWire[]
}

const folderWire: z.ZodType<FolderWire> = z.lazy(() =>
  z
    .object({
      id: z.union([z.string(), z.number()]).optional(),
      name: z.string().optional(),
      folderName: z.string().optional(),
      path: z.string().optional(),
      nestedFoldArr: z.array(folderWire).optional(),
    })
    .passthrough(),
)

export const folderListResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(folderWire).default([]),
})

export interface Folder {
  id: string
  name: string
  /** '/'-separated, leading slash — the same string a workbook row's `path` carries. */
  path: string
  children: Folder[]
}

function readFolder(w: FolderWire): Folder | null {
  const id = w.id === undefined ? '' : String(w.id)
  if (!id) return null
  const path = w.path ?? ''
  const name = w.name || w.folderName || path.split('/').filter(Boolean).pop() || id
  return {
    id,
    name,
    path,
    children: (w.nestedFoldArr ?? []).map(readFolder).filter((f): f is Folder => f !== null),
  }
}

export function readFolders(res: z.output<typeof folderListResponse>): Folder[] {
  return res.data.map(readFolder).filter((f): f is Folder => f !== null)
}

export interface FoldersApi {
  list: (q: { clusterId: string; scopeId: string }) => Promise<Folder[]>
}

export function createFoldersApi(client: ApiClient): FoldersApi {
  return {
    list: async ({ clusterId, scopeId }) => {
      const res = await client.request('POST', `/${clusterId}/api/directory/list`, {
        body: { scope_id: scopeId },
        schema: folderListResponse,
      })
      return readFolders(res)
    },
  }
}
